/**
 * Timer Store
 *
 * Standalone Zustand store for the global task timer.
 * Persisted to localStorage so the timer survives page refreshes.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { addTimeEntry } from '@/db/timeEntries';

interface TimerState {
  activeTaskId: string | null;
  activeTaskName: string | null;
  startedAt: number | null; // Date.now() when timer started
  accumulatedMs: number; // paused time accumulated before current run
  isRunning: boolean;
}

interface TimerActions {
  startTimer: (taskId: string, taskName: string) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  stopTimer: (userId: string) => Promise<number>; // returns total ms
  resetTimer: () => void;
  getElapsedMs: () => number;
}

type TimerStore = TimerState & TimerActions;

const initialState: TimerState = {
  activeTaskId: null,
  activeTaskName: null,
  startedAt: null,
  accumulatedMs: 0,
  isRunning: false,
};

export const useTimerStore = create<TimerStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      startTimer: (taskId, taskName) => {
        const state = get();
        // If timer is running for a different task, stop it first (discard)
        if (state.activeTaskId && state.activeTaskId !== taskId) {
          set(initialState);
        }
        set({
          activeTaskId: taskId,
          activeTaskName: taskName,
          startedAt: Date.now(),
          accumulatedMs: 0,
          isRunning: true,
        });
      },

      pauseTimer: () => {
        const state = get();
        if (!state.isRunning || !state.startedAt) return;
        const elapsed = Date.now() - state.startedAt;
        set({
          isRunning: false,
          startedAt: null,
          accumulatedMs: state.accumulatedMs + elapsed,
        });
      },

      resumeTimer: () => {
        const state = get();
        if (state.isRunning || !state.activeTaskId) return;
        set({
          isRunning: true,
          startedAt: Date.now(),
        });
      },

      stopTimer: async (userId) => {
        const state = get();
        if (!state.activeTaskId) return 0;

        const totalMs = state.getElapsedMs();
        const roundedMs = Math.round(totalMs / 60000) * 60000; // round to nearest minute

        if (roundedMs > 0) {
          try {
            const today = new Date().toISOString().split('T')[0];
            await addTimeEntry({
              taskId: state.activeTaskId,
              userId,
              date: today,
              duration: roundedMs,
            });
          } catch (err) {
            console.error('Failed to save time entry:', err);
          }
        }

        set(initialState);
        return roundedMs;
      },

      resetTimer: () => set(initialState),

      getElapsedMs: () => {
        const state = get();
        let total = state.accumulatedMs;
        if (state.isRunning && state.startedAt) {
          total += Date.now() - state.startedAt;
        }
        return total;
      },
    }),
    {
      name: 'zenda-timer',
      partialize: (state) => ({
        activeTaskId: state.activeTaskId,
        activeTaskName: state.activeTaskName,
        startedAt: state.startedAt,
        accumulatedMs: state.accumulatedMs,
        isRunning: state.isRunning,
      }),
    }
  )
);
